import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { MODULE_THEMES } from "../data/modules";
import { Sparkles, Target, CheckCircle2, RefreshCw, ClipboardList, ArrowRight, Sun } from "lucide-react";

export default function Plan() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [affirmation, setAffirmation] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    api.get("/goals").then((r) => {
      setData(r.data);
      setAffirmation(r.data.affirmation);
    });
  }, []);

  const newAffirmation = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/affirmation");
      setAffirmation(data.affirmation);
    } finally {
      setTimeout(() => setRefreshing(false), 400);
    }
  };

  if (!data)
    return (
      <div className="min-h-screen">
        <Navbar />
        <p className="text-center text-muted-foreground mt-20">Loading your plan…</p>
      </div>
    );

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-5xl mx-auto px-5 py-10">
        <h1 className="font-head font-extrabold text-3xl sm:text-4xl tracking-tight animate-fade-up">
          Weekly plan
        </h1>
        <p className="text-muted-foreground mt-2 animate-fade-up">
          Your goals, treatment focus, and a daily affirmation to carry with you.
        </p>

        {/* Affirmation */}
        <div
          className="mt-8 rounded-3xl p-8 border border-black/5 shadow-sm relative overflow-hidden animate-fade-up"
          style={{ backgroundColor: "#FCF3EB" }}
          data-testid="affirmation-card"
        >
          <div className="flex items-center gap-2 text-[#B45309] font-semibold text-sm mb-4">
            <Sun className="w-4 h-4" /> Today's affirmation
          </div>
          <p className="font-serif-accent text-2xl leading-relaxed text-[#2D3748] max-w-2xl" data-testid="affirmation-text">
            "{affirmation}"
          </p>
          <button
            onClick={newAffirmation}
            className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#B45309] hover:opacity-70 transition-opacity"
            data-testid="new-affirmation"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Another one
          </button>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          {/* Weekly goals */}
          <div className="bg-white rounded-3xl p-7 shadow-sm border border-black/5 animate-fade-up">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-head font-extrabold text-xl flex items-center gap-2">
                <Target className="w-5 h-5" style={{ color: "#5BA8A0" }} /> This week's goals
              </h2>
              <span className="text-sm font-bold text-muted-foreground" data-testid="goals-progress">
                {data.goals_completed}/{data.goals_total}
              </span>
            </div>
            <p className="text-sm text-muted-foreground mb-5">Resets every Monday. You've got this.</p>
            <div className="space-y-4">
              {data.goals.map((g) => {
                const pct = Math.round((g.current / g.target) * 100);
                return (
                  <div key={g.id} data-testid={`goal-${g.id}`}>
                    <div className="flex items-center justify-between text-sm font-semibold mb-1.5">
                      <span className="flex items-center gap-2">
                        {g.done && <CheckCircle2 className="w-4 h-4 text-[#48BB78]" />}
                        <span className={g.done ? "text-[#2F855A]" : ""}>{g.label}</span>
                      </span>
                      <span className="text-muted-foreground">
                        {g.current}/{g.target}
                      </span>
                    </div>
                    <div className="h-2.5 rounded-full bg-black/5 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${pct}%`,
                          backgroundColor: g.done ? "#48BB78" : "#5BA8A0",
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Treatment plan */}
          <div className="bg-white rounded-3xl p-7 shadow-sm border border-black/5 animate-fade-up">
            <h2 className="font-head font-extrabold text-xl flex items-center gap-2 mb-1">
              <ClipboardList className="w-5 h-5" style={{ color: "#9D8BB6" }} /> {data.treatment_plan.title}
            </h2>
            <p className="text-sm text-muted-foreground mb-5">A gentle roadmap tailored to your progress.</p>

            <div className="space-y-3 mb-6">
              {data.treatment_plan.steps.map((s, i) => (
                <div key={s} className="flex items-start gap-3" data-testid={`plan-step-${i}`}>
                  <span
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0 mt-0.5"
                    style={{ backgroundColor: "#9D8BB6" }}
                  >
                    {i + 1}
                  </span>
                  <p className="text-sm text-[#4A5568]">{s}</p>
                </div>
              ))}
            </div>

            <p className="text-sm font-semibold mb-3">Focus programs this week</p>
            <div className="space-y-2">
              {data.treatment_plan.focus.map((f) => {
                const t = MODULE_THEMES[f.key];
                return (
                  <button
                    key={f.key}
                    onClick={() => navigate(`/module/${f.key}`)}
                    className="w-full flex items-center justify-between rounded-2xl p-4 border border-black/5 hover:-translate-y-0.5 transition-transform text-left"
                    style={{ backgroundColor: t.bg }}
                    data-testid={`focus-${f.key}`}
                  >
                    <div>
                      <p className="font-head font-bold text-sm">{f.name}</p>
                      <p className="text-xs text-muted-foreground">{f.tagline}</p>
                    </div>
                    <ArrowRight className="w-4 h-4" style={{ color: t.primary }} />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
